import { useEffect, useRef } from "react";
import { EmployeeHeader, EmployeeMetrics, ActivitiesSection, CustomersSection } from "./ui";
import { EmployeeMetricsSkeleton } from "./ui/EmployeeMetricsSkeleton";
import { SidePanelContainer } from "../ui";
import { Divider } from "../../primitives";
import type TeamEmployee from "@acto/taskpane/core/domain/TeamEmployee";
import { TeamMeetingState } from "@acto/gql/graphql";
import { MANAGEMENT_TODO_TYPES } from "../../../utils/managementTodoTypes";
import { PeriodOption, DateRange } from "../../DateRangeSelector/types";
import { isComparableWindow, periodToDateRange } from "../../../utils/periodToDateRange";
import { useSingleEmployee } from "@acto/taskpane/core/data-access/team/useSingleEmployee";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";

export type ActivityType =
  | "plannedAppointments"
  | "noReportAppointments"
  | "documentedAppointments"
  | "didNotTakePlaceAppointments"
  | "ignoredSignals"
  | "markedDone"
  | "openTodos";

export const APPOINTMENT_STATE_BY_ACTIVITY: Partial<Record<ActivityType, TeamMeetingState>> = {
  plannedAppointments: TeamMeetingState.Planned,
  noReportAppointments: TeamMeetingState.NoReport,
  documentedAppointments: TeamMeetingState.Documented,
  didNotTakePlaceAppointments: TeamMeetingState.DidNotTakePlace,
};

interface EmployeeSidePanelProps {
  employee: TeamEmployee;
  locale: string;
  selectedPeriod?: PeriodOption;
  selectedCustomDateRange?: DateRange;
  onActivityClick: (activity: ActivityType) => void;
  onCustomerClick?: (customerId: string, customerName: string) => void;
  activeActivity?: ActivityType | null;
}

export const EmployeeSidePanel = ({
  employee,
  locale,
  selectedPeriod = PeriodOption.LAST_12_MONTHS,
  selectedCustomDateRange,
  onActivityClick,
  onCustomerClick,
  activeActivity,
}: EmployeeSidePanelProps) => {
  const effectiveDateRange = periodToDateRange(selectedPeriod, selectedCustomDateRange);
  const showTrends = isComparableWindow(selectedPeriod, selectedCustomDateRange);

  const { employee: employeeWithKpis, isFetching } = useSingleEmployee({
    employeeId: employee.id,
    dateRange: effectiveDateRange,
    todoTypes: MANAGEMENT_TODO_TYPES,
    withAppointmentStates: true,
  });

  const track = useTrack();

  const trackRef = useRef(track);
  const employeeNameRef = useRef(employee.name);
  useEffect(() => {
    trackRef.current = track;
    employeeNameRef.current = employee.name;
  });

  useEffect(() => {
    trackRef.current(AnalyticsEvents.MV_OPEN_EMPLOYEE_DETAILS, {
      employeeId: employee.id,
      employeeName: employeeNameRef.current,
    });
  }, [employee.id]);

  const activityTrackEvents = {
    plannedAppointments: AnalyticsEvents.MV_OPEN_SCHEDULED_APPOINTMENTS,
    noReportAppointments: AnalyticsEvents.MV_OPEN_NO_REPORT_APPOINTMENTS,
    documentedAppointments: AnalyticsEvents.MV_OPEN_COMPLETED_APPOINTMENTS,
    didNotTakePlaceAppointments: AnalyticsEvents.MV_OPEN_DID_NOT_TAKE_PLACE_APPOINTMENTS,
    ignoredSignals: AnalyticsEvents.MV_OPEN_IGNORED_CUSTOMERS,
    markedDone: AnalyticsEvents.MV_OPEN_MARKED_DONE_CUSTOMERS,
    openTodos: AnalyticsEvents.MV_OPEN_EMPLOYEE_TASKS,
  } as const;

  const handleActivityClick = (activity: ActivityType) => {
    track(activityTrackEvents[activity], {
      employeeId: employee.id,
      employeeName: employee.name,
    });
    onActivityClick(activity);
  };

  return (
    <SidePanelContainer>
      <EmployeeHeader name={employee.name} region={employee.region} />

      {isFetching ? (
        <EmployeeMetricsSkeleton />
      ) : employeeWithKpis ? (
        <EmployeeMetrics
          revenue={employeeWithKpis.revenue}
          margin={employeeWithKpis.margin}
          profit={employeeWithKpis.profit}
          locale={locale}
          showTrends={showTrends}
        />
      ) : (
        <EmployeeMetrics
          revenue={employee.revenue}
          margin={employee.margin}
          profit={employee.profit}
          locale={locale}
          showTrends={showTrends}
        />
      )}

      <Divider />

      {employeeWithKpis ? (
        <ActivitiesSection
          activityMetrics={employeeWithKpis.activityMetrics}
          onActivityClick={handleActivityClick}
          activeActivity={activeActivity}
          isLoading={isFetching}
        />
      ) : (
        <ActivitiesSection
          activityMetrics={employee.activityMetrics}
          onActivityClick={handleActivityClick}
          activeActivity={activeActivity}
          isLoading={isFetching}
        />
      )}

      <Divider />

      <CustomersSection
        employeeId={employee.id}
        dateRange={effectiveDateRange}
        locale={locale}
        onCustomerClick={onCustomerClick}
        showTrends={showTrends}
      />
    </SidePanelContainer>
  );
};
